import React, { Component } from "react";
import { Button } from "react-bootstrap";
import API from "../../services/API";

/**
 * The `event_registration` custom-form slot: the Yes / No event registration body
 * (SPEC 7.4 / 21.7). Replaced (and deleted) components/Dashboard/EventRegistration/EventRegistrationModal.
 *
 *   payload = { event_key, event: { key, title, event_date, venue, poster, acknowledgement[] } }
 *
 * Eligibility (flag, window, country, level, allowlist, already answered) is the engine's now:
 * nothing is fetched on mount, and the item is closable, so the frame offers the close X.
 *
 * - poster from REACT_APP_STORAGE_URL + "yep/" + poster (hidden if it fails to load), the
 *   acknowledgement paragraphs, "Will you be attending?" and the two answers
 * - Yes / No -> POST /event_registration/<event_key> {attending}; both buttons disabled while
 *   the call is pending (one request at a time)
 * - success: the server's message (or a default) replaces the question with a Close button;
 *   Close -> onComplete() so the "QR ticket has been sent" line is read before the item goes
 * - 409 (answered in another tab) is a message with Close, not an error. API.call rejects with its
 *   own {status, statusText, data, headers} (services/API.js:63-65, 162-169), so the status is
 *   error.status; error.response.status is kept as a fallback for a raw axios error
 * - any other failure keeps the question with the buttons enabled for a retry
 */
class EventRegistrationSlot extends Component {
  state = {
    submitting: false,
    message: null,
    error: null,
  };

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  respond = (attending) => {
    if (this.state.submitting) return;
    const eventKey = (this.props.payload || {}).event_key;
    this.setState({ submitting: true, error: null });

    API.call({
      method: "post",
      url: "/event_registration/" + eventKey,
      data: { attending },
    })
      .then((result) => {
        if (!this.mounted) return;
        this.setState({
          submitting: false,
          message: (result && result.data && result.data.message) || "Your response has been recorded.",
        });
      })
      .catch((error) => {
        if (!this.mounted) return;
        const status = error && error.status !== undefined
          ? error.status
          : (error && error.response ? error.response.status : undefined);
        if (status === 409) {
          // Already answered, for example in another browser tab.
          this.setState({ submitting: false, message: "You have already responded to this event." });
        } else if (status === 507) {
          // Registration limit reached (master PR #1034): show the server's message.
          const serverMessage = error && error.data && error.data.error && error.data.error.message;
          this.setState({ submitting: false, error: serverMessage || "Something went wrong. Please try again." });
        } else {
          this.setState({ submitting: false, error: "Something went wrong. Please try again." });
        }
      });
  };

  render() {
    const event = (this.props.payload || {}).event;
    if (!event) return null;
    const { submitting, message, error } = this.state;

    return (
      <div className="text-center event-registration-body">
        {event.poster ? (
          <img
            src={`${process.env.REACT_APP_STORAGE_URL}yep/${event.poster}`}
            alt={event.title}
            style={{ maxWidth: "100%", maxHeight: "45vh", marginBottom: "1rem" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : null}

        {message ? (
          <p data-testid="event-registration-message">{message}</p>
        ) : (
          <>
            {(event.acknowledgement || []).map((paragraph, i) => (
              <p key={i} className="text-left small" data-testid="event-registration-acknowledgement">{paragraph}</p>
            ))}
            <p><strong>Will you be attending?</strong></p>
          </>
        )}

        {error ? <p className="text-danger">{error}</p> : null}

        <div className="d-flex justify-content-center">
          {message ? (
            <Button variant="secondary" onClick={() => this.props.onComplete()}>Close</Button>
          ) : (
            <>
              <Button className="btn btn-primary-2" disabled={submitting} onClick={() => this.respond(true)}>
                Yes – Attending
              </Button>
              &nbsp;
              <Button variant="secondary" disabled={submitting} onClick={() => this.respond(false)}>
                No – Not Attending
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default EventRegistrationSlot;
